import { ApiError, apiFetch, parseErrorDetail } from "@/api/fetch";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Message, Session, SessionScope } from "../types";
import { queryKeys, sessionsQueryKey } from "../types";

// ---------------------------------------------------------------------------
// useSessions
// ---------------------------------------------------------------------------

const SESSIONS_PAGE_SIZE = 50;

interface SessionsResponse {
  sessions: Session[];
  has_more: boolean;
  total: number;
}

export const useSessions = (scope: SessionScope = "paperclip") => {
  const query = useInfiniteQuery({
    queryKey: sessionsQueryKey(scope),
    queryFn: async ({ pageParam = 0 }): Promise<SessionsResponse> => {
      const res = await apiFetch(
        `/chat/sessions?session_scope=${scope}&limit=${SESSIONS_PAGE_SIZE}&offset=${pageParam}`,
      );
      if (!res.ok) throw new ApiError(await parseErrorDetail(res), res.status);
      return (await res.json()) as SessionsResponse;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.reduce((sum, p) => sum + p.sessions.length, 0);
    },
  });

  return {
    ...query,
    data: query.data?.pages.flatMap((p) => p.sessions) ?? [],
  };
};

// ---------------------------------------------------------------------------
// useSessionMessages
// ---------------------------------------------------------------------------

const MESSAGE_GROUP_LIMIT = 5;

interface MessagesResponse {
  messages: Message[];
  has_more: boolean;
  next_before: string | null;
}

export const useSessionMessages = (sessionId: string | undefined) => {
  const { token } = useAuth();
  const [olderMessages, setOlderMessages] = useState<Message[]>([]);
  const [olderHasMore, setOlderHasMore] = useState(false);
  const [olderNextBefore, setOlderNextBefore] = useState<
    string | null | undefined
  >(undefined);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  const prevSessionRef = useRef(sessionId);

  useEffect(() => {
    if (sessionId !== prevSessionRef.current) {
      setOlderMessages([]);
      setOlderHasMore(false);
      setOlderNextBefore(undefined);
      prevSessionRef.current = sessionId;
    }
  }, [sessionId]);

  const query = useQuery({
    queryKey: queryKeys.messages(sessionId ?? ""),
    queryFn: async (): Promise<MessagesResponse> => {
      const res = await apiFetch(
        `/chat/sessions/${sessionId}/messages?group_limit=${MESSAGE_GROUP_LIMIT}`,
      );
      if (!res.ok) throw new ApiError(await parseErrorDetail(res), res.status);
      return (await res.json()) as MessagesResponse;
    },
    enabled: !!sessionId && !!token,
  });

  const loadOlderMessages = useCallback(async () => {
    if (!sessionId || loadingOlderRef.current) return;

    const cursor =
      olderNextBefore === undefined
        ? query.data?.next_before
        : olderNextBefore;
    if (!cursor) return;

    loadingOlderRef.current = true;
    setIsLoadingOlder(true);
    try {
      const res = await apiFetch(
        `/chat/sessions/${sessionId}/messages?group_limit=${MESSAGE_GROUP_LIMIT}&before=${encodeURIComponent(cursor)}`,
      );
      if (!res.ok) return;
      const older = (await res.json()) as MessagesResponse;
      setOlderMessages((prev) => [...older.messages, ...prev]);
      setOlderHasMore(older.has_more);
      setOlderNextBefore(older.next_before);
    } finally {
      loadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [sessionId, olderNextBefore, query.data?.next_before]);

  const messages = useMemo(() => {
    if (!query.data) return olderMessages;
    return [...olderMessages, ...query.data.messages];
  }, [query.data, olderMessages]);

  const hasMore =
    olderNextBefore === undefined
      ? (query.data?.has_more ?? false)
      : olderHasMore;

  const resetOlderMessages = useCallback(() => {
    setOlderMessages([]);
    setOlderHasMore(false);
    setOlderNextBefore(undefined);
  }, []);

  return {
    messages,
    hasMore,
    isLoading: !!sessionId && query.isPending,
    isError: query.isError,
    isLoadingOlder,
    loadOlderMessages,
    resetOlderMessages,
    refetch: query.refetch,
  };
};

// ---------------------------------------------------------------------------
// useDeleteSession
// ---------------------------------------------------------------------------

export const useDeleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await apiFetch(`/chat/sessions/${sessionId}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 404) {
        throw new ApiError(await parseErrorDetail(res), res.status);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });
};

// ---------------------------------------------------------------------------
// useRenameSession
// ---------------------------------------------------------------------------

export const useRenameSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      title,
    }: {
      sessionId: string;
      title: string;
    }) => {
      const res = await apiFetch(`/chat/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new ApiError(await parseErrorDetail(res), res.status);
      return (await res.json()) as { session_id: string; title: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });
};

// ---------------------------------------------------------------------------
// useSessionStatus
// ---------------------------------------------------------------------------

interface SessionStatus {
  status: "idle" | "running";
}

export const useSessionStatus = (
  sessionId: string | undefined,
  enabled: boolean,
) => {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.sessionStatus(sessionId ?? ""),
    queryFn: async (): Promise<SessionStatus> => {
      const res = await apiFetch(`/chat/sessions/${sessionId}/status`);
      if (!res.ok) throw new ApiError(await parseErrorDetail(res), res.status);
      return (await res.json()) as SessionStatus;
    },
    enabled: !!sessionId && !!token && enabled,
    refetchInterval: (query) => {
      if (!enabled) return false;
      const status = query.state.data?.status;
      if (status === "running") return 2000;
      return false;
    },
    staleTime: 1000,
  });
};

// ---------------------------------------------------------------------------
// useSupersedMessages
// ---------------------------------------------------------------------------

export const useSupersedMessages = (sessionId: string | undefined) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string) => {
      if (!sessionId) return;
      const res = await apiFetch(
        `/chat/sessions/${sessionId}/messages/${encodeURIComponent(messageId)}/edit`,
        { method: "POST" },
      );
      if (!res.ok) throw new ApiError(await parseErrorDetail(res), res.status);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.messages(sessionId),
      });
    },
    [sessionId, queryClient],
  );
};

// ---------------------------------------------------------------------------
// useForkSession
// ---------------------------------------------------------------------------

export const useForkSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await apiFetch(`/chat/sessions/${sessionId}/fork`, {
        method: "POST",
      });
      if (!res.ok) throw new ApiError(await parseErrorDetail(res), res.status);
      return ((await res.json()) as { session_id: string }).session_id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    },
  });
};

// ---------------------------------------------------------------------------
// cancelSession (plain function, not a hook)
// ---------------------------------------------------------------------------

export const cancelSession = async (sessionId: string): Promise<void> => {
  await apiFetch(`/chat/sessions/${sessionId}/cancel`, { method: "POST" });
};
